/* DateEditor.js */

"use strict";

const moment = require("moment");
const CellEditorBase = require("./CellEditorBase");
const {parseLocalDate, toUTCMidDayTimestamp} = require("./lib/localDate");

const DateControl = require("@gluenetworks/date_time_picker").DatePicker;

const VIEWPORT_MARGIN = 4;
const CELL_INSET = 11;

/**
 * @typedef {Object} DateEditorOptions
 * @property {?(string|number|Date)} minDate - earliest selectable day; the "today" sentinel
 *                                             resolves against the browser clock. Default: no limit.
 * @property {string} valueType - "localeDateString" (default) writes back what
 *                                `Date.toLocaleDateString()` produces; "timestamp" writes a number.
 * @property {boolean} utcMidDay - "timestamp" only: snap to 12:00 PM UTC, so the calendar date
 *                                 survives a timezone conversion on the wire.
 * @property {boolean} clearable - show the Clear action that resets the cell to empty.
 */
const DEFAULT_OPTIONS = {
    minDate: null,
    valueType: "localeDateString",
    utcMidDay: false,
    clearable: true,
    className: "date-editor",
};

/**
 * SlickGrid cell editor backed by the in-house date picker. Drop it onto any date column with
 * `editor: DateEditor`, and tune it per column with `editorOptions` (see DateEditorOptions).
 */
class DateEditor extends CellEditorBase {
    constructor(options) {
        super(options);

        this._config = Object.assign({}, DEFAULT_OPTIONS, options.column.editorOptions);
        if (this._config.className) {
            this.rootElement.classList.add(this._config.className);
        }
        this._minDate = this._resolveMinDate(this._config.minDate);
        this._selected = null;

        this._pickerRoot = this.input.renderer.rootElement;

        // bound once and kept, so _unregisterListeners removes these exact references
        this._onPickerRendered = this._onPickerRendered.bind(this);
        this._onPickerClick = this._onPickerClick.bind(this);
        this._onClearClick = this._onClearClick.bind(this);

        this._clearButton = this._config.clearable ? this._createClearButton() : null;
        this.input.addListener("Value", this._onPickerRendered);
        this._pickerRoot.addEventListener("click", this._onPickerClick, true);
        this._overrideMonthNavigation();

        this.loadValue(this._itemData);
        this._applyPosition();
    }

    _createInput() {
        const dateControl = new DateControl({darkTheme: true});
        // the picker builds no calendar until it is given a value, and rendering without one
        // throws - so seed it before anything reaches for the renderer
        dateControl.setValue(moment().toObject());
        // the picker root has to be focusable, otherwise focus stays on the grid canvas and
        // arrow keys move the active cell out from under the open editor
        dateControl.renderer.rootElement.tabIndex = -1;
        return dateControl;
    }

    _rootTagName() {
        return "date-cell-editor-gl";
    }

    _minimumSize({height}) {
        return {minHeight: `${height}px`};
    }

    _createClearButton() {
        const button = document.createElement("button");
        button.type = "button";
        button.classList.add("date-editor-clear");
        button.innerText = "Clear";
        button.addEventListener("click", this._onClearClick);
        this.rootElement.append(button);
        return button;
    }

    /**
     * The picker's own month stepping does not roll the year over at the December/January
     * boundary, which puts the next few months out of reach. Step whole months instead.
     * @private
     */
    _overrideMonthNavigation() {
        this.input.setPreviousMonth = () => this._shiftMonth(-1);
        this.input.setNextMonth = () => this._shiftMonth(1);
    }

    _shiftMonth(delta) {
        this._showMonthOf(moment(this.input.getValue()).add(delta, "month"));
    }

    /**
     * Move the calendar the picker is browsing. This is deliberately separate from the
     * selection: navigating months must not silently change the cell's value.
     * @param {?moment} day
     * @private
     */
    _showMonthOf(day) {
        this.input.setValue((day || moment()).toObject());
    }

    /**
     * @param {?(string|number|Date)} value
     * @returns {?moment}
     * @private
     */
    _resolveMinDate(value) {
        return value === "today" ? moment().startOf("day") : this._parseValue(value);
    }

    /**
     * Accept anything a date column may hold: a locale date string, an ISO string,
     * a Unix timestamp, a Date or a moment.
     * @param {*} value
     * @returns {?moment} - start of the parsed day, or null when empty/unparseable
     * @private
     */
    _parseValue(value) {
        if (value === null || value === undefined || value === "") {
            return null;
        }
        if (typeof value === "string") {
            const localDate = parseLocalDate(value);
            const parsed = localDate ? moment(localDate) : moment(value, moment.ISO_8601);
            return parsed.isValid() ? parsed.startOf("day") : null;
        }
        const parsed = moment(value);
        return parsed.isValid() ? parsed.startOf("day") : null;
    }

    /**
     * @param {?moment} selected
     * @returns {string|number|undefined} - the value written back to the grid item
     * @private
     */
    _serialize(selected) {
        const asTimestamp = this._config.valueType === "timestamp";
        if (!selected) {
            return asTimestamp ? undefined : "";
        }
        if (!asTimestamp) {
            return selected.toDate().toLocaleDateString();
        }
        return this._config.utcMidDay
            ? toUTCMidDayTimestamp(selected.toDate())
            : selected.valueOf();
    }

    _isSelectable(day) {
        return !this._minDate || !day.isBefore(this._minDate, "day");
    }

    _getValue() {
        return this._serialize(this._selected);
    }

    /**
     * The picker highlights days by day-of-month alone and has no notion of a minimum date,
     * so restate both against our own selection after every render.
     * @private
     */
    _repaint() {
        if (!this._pickerRoot) {
            return;
        }
        this._pickerRoot.querySelectorAll("calendar-day-gl").forEach((element) => {
            element.classList.remove("selected");
            const day = element._day && moment(element._day.moment);
            if (!day) {
                return;
            }
            if (!this._isSelectable(day)) {
                element.classList.remove("selectable");
                element.classList.add("unselectable");
            }
            if (this._selected && day.isSame(this._selected, "day")) {
                element.classList.add("selected");
            }
        });
        if (this._clearButton) {
            this._clearButton.disabled = !this._selected;
        }
    }

    _onPickerRendered() {
        this._repaint();
    }

    /**
     * Handle day picks ourselves in the capture phase: it keeps days before `minDate` inert,
     * and separates a real selection from the month/year navigation that also moves the value.
     * @param {MouseEvent} e
     * @private
     */
    _onPickerClick(e) {
        const dayElement = e.target.closest && e.target.closest("calendar-day-gl");
        if (!dayElement || !dayElement._day) {
            return; // navigation chevrons, year list, calendar chrome
        }

        e.preventDefault();
        e.stopPropagation();

        const day = moment(dayElement._day.moment).startOf("day");
        if (!this._isSelectable(day)) {
            return;
        }
        this._selected = day;
        this._showMonthOf(day);
        // picking a day is the whole interaction - commit then close
        this._saveAndClose();
    }

    _onClearClick(e) {
        e.preventDefault();
        e.stopPropagation();
        this._selected = null;
        this._saveAndClose();
    }

    _onRootKeydown(e) {
        const key = e.key.toLowerCase();

        if (key.startsWith("arrow")) {
            // the picker has no keyboard day navigation - swallow arrows so the grid
            // does not move the active cell out from under the open editor
            e.stopPropagation();
            return;
        }
        if (key === "enter") {
            e.stopPropagation();
            this._saveAndClose();
            return;
        }
        if (key === "escape") {
            this._cancel();
        }
    }

    _cancel() {
        this._selected = this._parseValue(this.initialData);
        this._showMonthOf(this._selected);
        this._repaint();
        this.cancelChanges();
    }

    _unregisterListeners() {
        if (this.input) {
            this.input.removeListener("Value", this._onPickerRendered);
        }
        if (this._pickerRoot) {
            this._pickerRoot.removeEventListener("click", this._onPickerClick, true);
        }
        if (this._clearButton) {
            this._clearButton.removeEventListener("click", this._onClearClick);
        }
        super._unregisterListeners();
    }

    /*****************************/
    /*       PUBLIC API          */
    /*****************************/

    /**
     * The picker is wider than most grid cells, so center it on the cell and keep it inside
     * the viewport instead of letting it run off an edge. Called again by the grid on scroll.
     * @param {SlickPosition} cellPosition
     */
    position(cellPosition) {
        this._cellPosition = cellPosition;
        this._applyPosition();
    }

    _applyPosition() {
        if (!this.rootElement || !this._cellPosition) {
            return;
        }
        const {top, left, width} = this._cellPosition;
        const {offsetWidth, offsetHeight} = this.rootElement;
        const centeredLeft = left + (width - offsetWidth) / 2;
        const insetTop = top + CELL_INSET;
        const minLeft = window.pageXOffset + VIEWPORT_MARGIN;
        const minTop = window.pageYOffset + VIEWPORT_MARGIN;
        const maxLeft =
            window.pageXOffset +
            document.documentElement.clientWidth -
            offsetWidth -
            VIEWPORT_MARGIN;
        const maxTop =
            window.pageYOffset +
            document.documentElement.clientHeight -
            offsetHeight -
            VIEWPORT_MARGIN;

        Object.assign(this.rootElement.style, {
            top: `${Math.max(minTop, Math.min(insetTop, maxTop))}px`,
            left: `${Math.max(minLeft, Math.min(centeredLeft, maxLeft))}px`,
        });
    }

    loadValue(item) {
        if (!this.input) {
            return;
        }
        this.initialData = item[this._fieldName];
        this._selected = this._parseValue(this.initialData);
        this._showMonthOf(this._selected);
        this._repaint();
    }

    isValueChanged() {
        const initial = this._parseValue(this.initialData);
        if (!initial || !this._selected) {
            return Boolean(initial) !== Boolean(this._selected);
        }
        return !initial.isSame(this._selected, "day");
    }

    destroy() {
        return super.destroy().then(() => {
            this._pickerRoot = null;
            this._clearButton = null;
            this._selected = null;
            this._minDate = null;
        });
    }
}

module.exports = DateEditor;
